import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Plus, Users2, Wallet, CircleCheck, CircleAlert, Ban, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

// Small badge that colours by referrer/commission status
function Pill({ tone = "muted", children }) {
  const map = {
    active: "bg-primary/10 text-primary",
    paused: "bg-yellow-500/15 text-yellow-700",
    terminated: "bg-destructive/15 text-destructive",
    pending: "bg-yellow-500/15 text-yellow-700",
    approved: "bg-blue-500/15 text-blue-700",
    paid: "bg-primary/10 text-primary",
    clawed_back: "bg-destructive/15 text-destructive",
    muted: "bg-muted text-muted-foreground",
  };
  return <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${map[tone] || map.muted}`}>{children}</span>;
}

export default function Referrers() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [structures, setStructures] = useState([]);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(null);   // referrer being viewed in drawer

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (filterType && filterType !== "all") params.type_code = filterType;
    const { data } = await api.get("/admin/referrers", { params });
    setRows(data);
  }, [q, filterType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/masters/lookup/referrer_type").then((r) => setTypes(r.data || [])).catch(() => setTypes([]));
    api.get("/masters/lookup/commission_structure").then((r) => setStructures(r.data || [])).catch(() => setStructures([]));
  }, []);

  const structureById = useMemo(() => {
    const m = {};
    for (const s of structures) m[s.id] = s;
    return m;
  }, [structures]);

  const copyLink = (code) => {
    const url = `${window.location.origin}/signup?ref=${code}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Share link copied"));
  };

  return (
    <div className="space-y-6" data-testid="crm-referrers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline text-secondary">CRM</div>
          <h2 className="mt-1 font-display text-3xl font-light tracking-tight">Referrers & commissions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            One table for agents, customers, employees & partners. Percentages come from&nbsp;
            <a className="text-primary hover:underline" href="/app/admin/masters" data-testid="link-commission-master">commission_structure master</a>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="referrer-import-btn" onClick={() => setOpenImport(true)}>
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button data-testid="referrer-new-btn" onClick={() => setOpenNew(true)}>
            <Plus className="w-4 h-4 mr-2" /> New referrer
          </Button>
        </div>
      </div>

      <Dialog open={openImport} onOpenChange={(o) => { setOpenImport(o); if (!o) { setCsvText(""); setImportResult(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk import referrers</DialogTitle>
            <DialogDescription>
              Paste CSV with headers: <code>email,name,type_code,phone,commission_structure_id,notes</code>. Duplicates by email are skipped.
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full h-40 border border-input rounded-md p-2 text-xs font-mono"
            placeholder="email,name,type_code&#10;alice@acme.com,Alice Smith,agent&#10;bob@shop.co,Bob Lee,employee"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            data-testid="referrer-import-textarea"
          />
          {importResult && (
            <div className="text-sm">
              ✅ Created: <b>{importResult.created}</b> · Skipped (dup): <b>{importResult.skipped}</b> · Failed: <b>{importResult.failed?.length || 0}</b>
              {importResult.failed?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto text-xs text-rose-500">
                  {importResult.failed.map((f, i) => <div key={i}>{f.reason}: {JSON.stringify(f.row)}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="referrer-import-submit"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/referrers/import", { csv: csvText });
                  setImportResult(data);
                  toast.success(`Imported ${data.created} referrers`);
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
              }}
              disabled={!csvText.trim()}
            >Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="Search name / email / code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="referrer-search-input"
          className="sm:max-w-sm"
        />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="sm:max-w-[220px]" data-testid="referrer-filter-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => (
              <SelectItem key={t.id} value={t.data?.code || t.label.toLowerCase()}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-muted/40 text-[11px] overline">
          <div className="col-span-4">Referrer</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Commission plan</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {rows.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No referrers yet. Add your first one to start attributing commissions.
          </div>
        )}
        {rows.map((r) => {
          const structure = structureById[r.commission_structure_id];
          return (
            <button
              key={r.id}
              onClick={() => setSelected(r)}
              className="w-full text-left grid grid-cols-12 gap-2 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors"
              data-testid={`referrer-row-${r.id}`}
            >
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  <span className="font-mono">{r.code}</span>{r.email ? ` · ${r.email}` : ""}
                </div>
              </div>
              <div className="col-span-2 text-sm capitalize self-center">{r.type_code}</div>
              <div className="col-span-3 text-sm self-center truncate">
                {structure?.label || <span className="text-muted-foreground">—</span>}
              </div>
              <div className="col-span-2 self-center"><Pill tone={r.status}>{r.status}</Pill></div>
              <div className="col-span-1 text-right self-center">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); copyLink(r.code); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); copyLink(r.code); }}}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted cursor-pointer"
                  data-testid={`referrer-copy-${r.id}`}
                  title="Copy share link"
                >
                  <Copy className="w-4 h-4" />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <NewReferrerDialog
        open={openNew}
        onOpenChange={setOpenNew}
        types={types}
        structures={structures}
        onCreated={() => { setOpenNew(false); load(); toast.success("Referrer added"); }}
      />

      <ReferrerDrawer
        referrer={selected}
        onClose={() => setSelected(null)}
        structureById={structureById}
        onChanged={load}
      />
    </div>
  );
}

function NewReferrerDialog({ open, onOpenChange, types, structures, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", type_code: "agent",
    commission_structure_id: "", payout_method: "bank_transfer", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Filter structures by type_code so admin can't accidentally attach agent
  // rules to a customer referrer, etc.
  const filteredStructures = useMemo(() => {
    return structures.filter((s) => (s.data?.referrer_type_code || "").toLowerCase() === form.type_code);
  }, [structures, form.type_code]);

  useEffect(() => {
    if (filteredStructures.length && !filteredStructures.find((s) => s.id === form.commission_structure_id)) {
      setForm((f) => ({ ...f, commission_structure_id: filteredStructures[0].id }));
    }
  }, [filteredStructures, form.commission_structure_id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        type_code: form.type_code,
        commission_structure_id: form.commission_structure_id,
        payout_method: form.payout_method,
        notes: form.notes || null,
      };
      await api.post("/admin/referrers", payload);
      setForm({ name: "", email: "", phone: "", type_code: "agent", commission_structure_id: "", payout_method: "bank_transfer", notes: "" });
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">New referrer</DialogTitle>
          <DialogDescription>Anyone who can bring you paying customers — agents, existing customers, employees, partners.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input required data-testid="referrer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" data-testid="referrer-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input data-testid="referrer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v, commission_structure_id: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="referrer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.data?.code || t.label.toLowerCase()}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payout method</Label>
              <Select value={form.payout_method} onValueChange={(v) => setForm({ ...form, payout_method: v })}>
                <SelectTrigger className="mt-1.5" data-testid="referrer-payout-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="credit_balance">Account credit</SelectItem>
                  <SelectItem value="stripe_connect">Stripe Connect (auto)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Commission plan (from master)</Label>
            <Select value={form.commission_structure_id} onValueChange={(v) => setForm({ ...form, commission_structure_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="referrer-structure-select">
                <SelectValue placeholder={filteredStructures.length ? "Choose plan" : "No plans for this type — add one in masters"} />
              </SelectTrigger>
              <SelectContent>
                {filteredStructures.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.commission_structure_id && (() => {
              const s = filteredStructures.find((x) => x.id === form.commission_structure_id);
              if (!s) return null;
              const d = s.data || {};
              return (
                <div className="mt-2 text-xs text-muted-foreground p-3 rounded-md bg-muted/40 border border-border">
                  First: <strong>{d.first_period_pct || 0}%</strong> · Recurring: <strong>{d.recurring_pct || 0}%</strong> ·
                  Customer discount: <strong>{d.customer_discount_value || 0} {d.customer_discount_type || ""}</strong> ·
                  Clawback: <strong>{d.clawback_days || 0} days</strong>
                </div>
              );
            })()}
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input data-testid="referrer-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
          </div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <DialogFooter>
            <Button type="submit" data-testid="referrer-submit-btn" disabled={saving}>
              {saving ? "Adding…" : "Add referrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReferrerDrawer({ referrer, onClose, structureById, onChanged }) {
  const [tab, setTab] = useState("overview");
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [checked, setChecked] = useState(new Set());

  const load = useCallback(async () => {
    if (!referrer) return;
    const [c, p] = await Promise.all([
      api.get("/admin/commissions", { params: { referrer_id: referrer.id } }),
      api.get("/admin/payouts", { params: { referrer_id: referrer.id } }),
    ]);
    setCommissions(c.data || []);
    setPayouts(p.data || []);
    setChecked(new Set());
  }, [referrer]);

  useEffect(() => { if (referrer) load(); }, [referrer, load]);

  if (!referrer) return null;
  const s = structureById[referrer.commission_structure_id];
  const totalPending = commissions.filter(c => c.status === "pending").reduce((a, b) => a + b.amount_gbp, 0);
  const totalApproved = commissions.filter(c => c.status === "approved").reduce((a, b) => a + b.amount_gbp, 0);
  const totalPaid = commissions.filter(c => c.status === "paid").reduce((a, b) => a + b.amount_gbp, 0);

  const toggle = (id) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const approveOne = async (cid) => {
    await api.post(`/admin/commissions/${cid}/approve`); load();
    toast.success("Approved");
  };
  const clawbackOne = async (cid) => {
    if (!window.confirm("Claw back this commission?")) return;
    await api.post(`/admin/commissions/${cid}/clawback`); load();
    toast.success("Clawed back");
  };
  const payout = async () => {
    if (!checked.size) return;
    try {
      await api.post("/admin/payouts", {
        referrer_id: referrer.id,
        method: referrer.payout_method || "bank_transfer",
        commission_ids: Array.from(checked),
      });
      toast.success("Payout recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const setStatus = async (status) => {
    await api.put(`/admin/referrers/${referrer.id}`, { status });
    toast.success(`Referrer ${status}`);
    onChanged();
    onClose();
  };

  return (
    <Dialog open={!!referrer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">
            {referrer.name} <span className="ml-2 font-mono text-sm text-muted-foreground">{referrer.code}</span>
          </DialogTitle>
          <DialogDescription>
            <Pill tone={referrer.status}>{referrer.status}</Pill>
            <span className="ml-2 text-xs capitalize">{referrer.type_code}</span>
            {s && <span className="ml-2 text-xs">· plan: {s.label}</span>}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList>
            <TabsTrigger value="overview" data-testid="drawer-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="drawer-tab-commissions">Commissions ({commissions.length})</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="drawer-tab-payouts">Payouts ({payouts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-3 pt-3">
            <div className="grid grid-cols-3 gap-3">
              <StatCard icon={<CircleAlert className="w-4 h-4" />} label="Pending" value={`£${totalPending.toFixed(2)}`} />
              <StatCard icon={<CircleCheck className="w-4 h-4" />} label="Approved" value={`£${totalApproved.toFixed(2)}`} />
              <StatCard icon={<Wallet className="w-4 h-4" />} label="Paid" value={`£${totalPaid.toFixed(2)}`} />
            </div>
            <div className="text-xs text-muted-foreground p-3 rounded-md bg-muted/40 border border-border">
              <div>Share link: <span className="font-mono">{window.location.origin}/signup?ref={referrer.code}</span></div>
              <div className="mt-1">Payout method: <strong className="capitalize">{referrer.payout_method?.replace(/_/g, " ")}</strong></div>
              {referrer.notes && <div className="mt-1">Notes: {referrer.notes}</div>}
            </div>
            <div className="flex gap-2 pt-2">
              {referrer.status !== "active" && <Button size="sm" variant="outline" onClick={() => setStatus("active")}><CircleCheck className="w-4 h-4 mr-1" /> Reactivate</Button>}
              {referrer.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus("paused")}><CircleAlert className="w-4 h-4 mr-1" /> Pause</Button>}
              {referrer.status !== "terminated" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setStatus("terminated")}><Ban className="w-4 h-4 mr-1" /> Terminate</Button>}
            </div>
          </TabsContent>

          <TabsContent value="commissions" className="pt-3">
            <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border">
              {commissions.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No commissions yet.</div>}
              {commissions.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 text-sm" data-testid={`commission-${c.id}`}>
                  <input
                    type="checkbox"
                    disabled={c.status !== "approved"}
                    checked={checked.has(c.id)}
                    onChange={() => toggle(c.id)}
                    data-testid={`commission-check-${c.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">£{c.amount_gbp.toFixed(2)} <span className="text-muted-foreground text-xs">· {c.period}</span></div>
                    <div className="text-xs text-muted-foreground truncate">sub {c.subscription_id.slice(0, 8)}…  · created {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <Pill tone={c.status}>{c.status}</Pill>
                  <div className="flex gap-1">
                    {c.status === "pending" && <Button size="sm" variant="ghost" onClick={() => approveOne(c.id)}>Approve</Button>}
                    {["pending", "approved"].includes(c.status) && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => clawbackOne(c.id)}>Claw back</Button>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button data-testid="payout-btn" disabled={!checked.size} onClick={payout}>
                <Wallet className="w-4 h-4 mr-2" /> Pay out {checked.size} selected
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="payouts" className="pt-3">
            <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border">
              {payouts.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No payouts yet.</div>}
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 text-sm">
                  <div className="flex-1">
                    <div className="font-medium">£{p.total_gbp.toFixed(2)} <span className="text-muted-foreground text-xs">· {p.method.replace(/_/g, " ")}</span></div>
                    <div className="text-xs text-muted-foreground">{new Date(p.paid_at).toLocaleString()} · {p.commission_ids.length} commissions</div>
                  </div>
                  {p.bank_reference && <span className="text-xs font-mono">{p.bank_reference}</span>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</div>
      <div className="mt-1 font-display text-xl">{value}</div>
    </div>
  );
}
